import React from 'react';
import { Link } from 'react-router-dom';
import LocLink from '../components/LocLink';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { FaEtsy } from 'react-icons/fa';
import { trackProjectActClick } from '../lib/analytics';
import ProductLimitationsNotice from '../components/ProductLimitationsNotice';
import { ETSY_SHOP_URL, getTrustStats } from '../config/trustStats';
import SEOHead from '../components/SEOHead';
import JsonLd, { organizationSchema, websiteSchema } from '../components/JsonLd';
import { getLocalizedImage, getLocalizedAlt } from '../utils/getLocalizedImage';
import USTariffNotice from '../components/USTariffNotice';
import EtsyReviews from '../components/EtsyReviews';
import { useUSTariffNotice } from '../hooks/useIsUSVisitor';
import useEtsyLive from '../hooks/useEtsyLive';
import {
  Shield,
  Thermometer,
  Zap,
  Brain,
  Heart,
  AlertTriangle,
  Pill,
  Stethoscope,
  User,
  Users,
  HeartHandshake,
  Ambulance,
  Star,
  CheckCircle,
  Quote,
  ChevronRight,
  ArrowRight,
  ArrowDown,
  ShieldCheck,
  Backpack,
  Timer,
} from 'lucide-react';

const IMAGES = {
  heroBanner: '/images/wqlk87v9_banner_mgp-Photoroom.jpg',
  epipod: 'https://us.chat-img.sintra.ai/7edd9b7a-9f5a-467d-9e3a-be6d0559b75f/03732df8-b45c-492b-b164-0fc6530010bb/epipod_drop_water_-_Copie.png',
  tempswing: '/images/0zge02g6_tempswing-Photoroom.jpg',
  nayzipod: '/images/bgl1ur09_MGP_Nayzipod.png',
  lifestyle: '/images/1uef6ln1_68c7a61c-0ca1-4ea0-9fc7-3e90a4fdd063.png',
  quebec: 'https://images.unsplash.com/photo-1691516094722-3b6662d72140?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxxdWViZWMlMjBsYW5kc2NhcGUlMjBuYXR1cmV8ZW58MHx8fHwxNzc2MzUwMjkyfDA&ixlib=rb-4.1.0&q=85',
};

const HomePage = () => {
  const { t, language } = useLanguage();

  const etsyLive = useEtsyLive();
  const trustStats = getTrustStats(language, etsyLive);
  const tariffNotice = useUSTariffNotice();
  const stats = [
    { key: 'sales', value: trustStats.sales.value, label: trustStats.sales.label },
    { key: 'rating', value: trustStats.rating.value, suffix: trustStats.rating.suffix, label: trustStats.rating.label, icon: Star },
    { key: 'reviews', value: trustStats.reviews.value, label: trustStats.reviews.label },
    { key: 'listings', value: trustStats.listings.value, label: trustStats.listings.label },
  ];

  const features = [
    {
      key: 'impact',
      icon: Shield,
      image: IMAGES.epipod,
    },
    {
      key: 'access',
      icon: Zap,
      image: IMAGES.nayzipod,
    },
  ];

  const RIBBON_IMAGES = {
    epilepsy: '/images/l37azf4s_ChatGPT_Image_16_avr._2026_13_30_55.png',
    adrenal: '/images/pcbe2bml_ChatGPT_Image_16_avr._2026_13_33_33.png',
    allergy: '/images/k2bpgr2g_ChatGPT_Image_16_avr._2026_13_56_35.png',
    heart: '/images/0irdfe9w_ChatGPT_Image_16_avr._2026_14_24_08.png',
    overdose: '/images/cp9isp58_ChatGPT_Image_16_avr._2026_14_26_19.png',
  };

  const productCategories = [
    { key: 'epilepsy', ribbon: RIBBON_IMAGES.epilepsy, etsyUrl: 'https://www.etsy.com/shop/MyGuardPod?ref=seller-platform-mcnav&section_id=57806717' },
    { key: 'adrenal', ribbon: RIBBON_IMAGES.adrenal, etsyUrl: 'https://www.etsy.com/shop/MyGuardPod?ref=seller-platform-mcnav&section_id=57806739' },
    { key: 'allergy', ribbon: RIBBON_IMAGES.allergy, etsyUrl: 'https://www.etsy.com/shop/MyGuardPod?ref=seller-platform-mcnav&section_id=57791610' },
    { key: 'heart', ribbon: RIBBON_IMAGES.heart, etsyUrl: 'https://www.etsy.com/shop/MyGuardPod?ref=seller-platform-mcnav&section_id=57806719' },
    { key: 'overdose', ribbon: RIBBON_IMAGES.overdose, etsyUrl: 'https://www.etsy.com/shop/MyGuardPod?ref=seller-platform-mcnav&section_id=53896058' },
    { key: 'asthma', ribbon: '/images/gn91kotm_ChatGPT_Image_16_avr._2026_14_30_36.png', etsyUrl: 'https://myguardpod.etsy.com/listing/4450337764' },
  ];

  const audiences = [
    { key: 'patients', icon: User },
    { key: 'parents', icon: Users },
    { key: 'caregivers', icon: HeartHandshake },
    { key: 'healthcare', icon: Stethoscope },
    { key: 'responders', icon: Ambulance },
  ];

  return (
    <main className="pt-16 sm:pt-20">
      <SEOHead
        routeKey="home"
        language={language}
        title={language === 'fr'
          ? 'MyGuardPod — Étuis protecteurs pour médicaments d’urgence'
          : 'MyGuardPod — Protective Carrying Cases for Emergency Medications'}
        description={language === 'fr'
          ? 'Étuis protecteurs de transport conçus pour aider à transporter, organiser et identifier les contenants de médicaments d’urgence compatibles. Fabriqués sur commande au Québec, Canada. Médicament non inclus.'
          : 'Purpose-built carrying cases designed to help carry, organize and identify compatible emergency-medication containers. Made to order in Québec, Canada. Medication not included.'}
      />
      <JsonLd data={organizationSchema(language)} />
      <JsonLd data={websiteSchema(language)} />
      {/* Semantic H1 for accessibility & SEO — visually hidden to preserve the banner-led hero */}
      <h1 className="sr-only">
        {language === 'fr'
          ? 'Étuis protecteurs pour médicaments d\u2019urgence'
          : 'Protective carrying cases for emergency medications'}
      </h1>
      {/* Hero Section - Full Width Banner */}
      <section className="overflow-hidden banner-fade-dark banner-fade">
        <img
          src={getLocalizedImage('homeHero', language)}
          alt={getLocalizedAlt('homeHero', language)}
          className="w-full h-auto object-cover"
        />
      </section>

      {/* Stats & CTAs */}
      <section className="bg-[#1a1a1a] py-8 sm:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center text-center"
          >
            <p
              className="font-manrope text-base sm:text-lg text-white/90 max-w-2xl mb-6 leading-relaxed"
              data-testid="hero-subtitle"
            >
              {t('hero.subtitle')}
            </p>
            {/* Stats Row — links to Etsy shop for verified source */}
            <a
              href={ETSY_SHOP_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={trustStats.linkLabel}
              className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-4 sm:gap-8 mb-3 group rounded-2xl px-3 py-2 -mx-3 -my-2 hover:bg-white/5 transition-colors"
              data-testid="trust-stats-etsy-link"
            >
              {stats.map((stat) => (
                <motion.div
                  key={stat.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center"
                >
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <span className="font-outfit font-black text-xl sm:text-3xl text-white">
                      {stat.value}
                      {stat.suffix && (
                        <span className="text-base sm:text-xl text-white/70">{stat.suffix}</span>
                      )}
                    </span>
                    {stat.icon && (
                      <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#E31B23] fill-[#E31B23]" />
                    )}
                  </div>
                  <span className="font-manrope text-[10px] sm:text-sm text-white/80 uppercase tracking-wide">
                    {stat.label}
                  </span>
                </motion.div>
              ))}
            </a>

            {/* Source note */}
            <p
              className="font-manrope text-[10px] sm:text-xs text-white/50 mb-6"
              data-testid="stats-source-note"
            >
              {trustStats.sourceNote}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 w-full sm:w-auto">
              <a
                href="https://www.etsy.com/shop/MyGuardPod"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 bg-[#E31B23] hover:bg-[#c8171e] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 active:scale-95 shadow-lg text-sm sm:text-base"
                data-testid="hero-shop-cta"
              >
                <FaEtsy className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>{t('hero.cta')}</span>
              </a>
              <LocLink
                to="products"
                className="inline-flex items-center justify-center space-x-2 border-2 border-white text-white hover:bg-white hover:text-[#1a1a1a] px-6 py-3 rounded-full font-bold transition-all duration-300 text-sm sm:text-base"
                data-testid="hero-view-products"
              >
                <span>{t('hero.ctaSecondary')}</span>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </LocLink>
            </div>

            {/* Short factual disclaimer */}
            <p
              className="font-manrope text-[11px] sm:text-xs text-white/60 mt-5 max-w-xl leading-relaxed"
              data-testid="hero-short-disclaimer"
            >
              {language === 'fr'
                ? 'Accessoires de transport protecteurs seulement. M\u00e9dicaments et fournitures m\u00e9dicales non inclus. Aucun contr\u00f4le de la temp\u00e9rature.'
                : 'Protective carrying accessories only. Medication and medical supplies are not included. No temperature control.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <section className="bg-[#1a1a1a] py-4 overflow-x-hidden">
        <Marquee speed={40} gradient={false}>
          <span className="font-mono text-sm text-neutral-400 tracking-widest px-8">
            {t('marquee')} {'\u2022'} {t('marquee')} {'\u2022'}
          </span>
        </Marquee>
      </section>

      {/* U.S. customer notice (only shown to detected U.S. visitors) */}
      {tariffNotice.show && (
        <section className="bg-white py-8 sm:py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <USTariffNotice onDismiss={tariffNotice.dismiss} />
          </div>
        </section>
      )}

      {/* Features Section - Bento Grid */}
      <section id="features" className="py-24 sm:py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1a1a1a] mb-4">
              {t('features.title')}
            </h2>
            <p className="font-manrope text-lg text-neutral-600 max-w-2xl mx-auto">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={feature.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-[#1a1a1a] rounded-3xl overflow-hidden hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={feature.image}
                    alt={t(`features.${feature.key}.title`)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-[#E31B23] rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-outfit font-bold text-xl text-white mb-2">
                    {t(`features.${feature.key}.title`)}
                  </h3>
                  <p className="font-manrope text-neutral-400 leading-relaxed">
                    {t(`features.${feature.key}.description`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Medication storage banner — secondary, sober, factual (no thermal claim) */}
          <div
            className="mt-8 max-w-5xl mx-auto rounded-2xl border border-neutral-800 bg-neutral-900/60 px-5 py-5 sm:px-7 sm:py-6 flex items-start gap-4"
            data-testid="medication-storage-banner"
            role="note"
          >
            <Thermometer className="w-5 h-5 sm:w-6 sm:h-6 text-neutral-300 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="min-w-0">
              <h3 className="font-outfit font-bold text-sm sm:text-base text-white mb-1.5">
                {language === 'fr' ? 'Conservation du médicament' : 'Medication storage'}
              </h3>
              <p className="font-manrope text-xs sm:text-sm text-neutral-300 leading-relaxed">
                {language === 'fr'
                  ? 'Les étuis MyGuardPod standards ne contrôlent pas la température. Respectez toujours les conditions de conservation indiquées par le fabricant du médicament ou votre pharmacien. Évitez l’exposition prolongée au soleil direct, au gel et à la chaleur excessive.'
                  : 'Standard MyGuardPod cases do not control temperature. Always follow the storage conditions provided by the medication manufacturer or your pharmacist. Avoid prolonged exposure to direct sunlight, freezing temperatures and excessive heat.'}
              </p>
            </div>
          </div>

          <p className="text-center mt-10 font-manrope text-sm text-neutral-400 max-w-2xl mx-auto">
            {t('features.compatible')}
          </p>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1a1a1a] mb-4">
              {t('products.title')}
            </h2>
            <p className="font-manrope text-lg text-neutral-600 max-w-2xl mx-auto">
              {t('products.subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {productCategories.map((category, index) => (
              <motion.div
                key={category.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <a
                  href={category.etsyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group p-6 border-2 border-neutral-200 rounded-2xl hover:border-[#E31B23] hover:shadow-lg transition-all duration-300"
                  data-testid={`category-${category.key}`}
                >
                  {category.ribbon ? (
                    <img src={category.ribbon} alt={t(`products.categories.${category.key}.title`)} className="w-16 h-16 object-contain mb-4" />
                  ) : (
                    <div className={`w-12 h-12 ${category.color} rounded-xl flex items-center justify-center mb-4`}>
                      <category.icon className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <h3 className="font-outfit font-bold text-lg text-[#1a1a1a] mb-2 group-hover:text-[#E31B23] transition-colors">
                    {t(`products.categories.${category.key}.title`)}
                  </h3>
                  <p className="font-manrope text-sm text-neutral-500">
                    {t(`products.categories.${category.key}.products`)}
                  </p>
                </a>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="https://www.etsy.com/shop/MyGuardPod"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-[#E31B23] hover:text-[#c8171e] font-bold transition-colors"
              data-testid="view-all-products"
            >
              <span>{t('products.viewAll')}</span>
              <ChevronRight className="w-5 h-5" />
            </a>
          </div>

          <p className="text-center mt-8 font-manrope text-sm text-neutral-500 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Formats con\u00e7us pour des contenants compatibles de m\u00e9dicaments comme Valtoco, Nayzilam, EpiPen, Naloxone, nitroglyc\u00e9rine et les kits d\u2019hydrocortisone injectable d\u2019urgence.'
              : 'Formats designed for compatible containers of medications such as Valtoco, Nayzilam, EpiPen, Naloxone, nitroglycerin and emergency hydrocortisone injection kits.'}
          </p>
        </div>
      </section>

      {/* Featured Initiative — Project ACT */}
      <section className="py-20 sm:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <LocLink
              to="projectAct"
              onClick={() => trackProjectActClick('home_featured')}
              className="group block bg-white rounded-3xl overflow-hidden ring-1 ring-[#E31B23]/30 hover:ring-[#E31B23]/60 hover:shadow-xl transition-all duration-300"
              data-testid="home-featured-project-act"
            >
              <div className="grid lg:grid-cols-2">
                {/* Image */}
                <div className="relative aspect-[4/3] lg:aspect-auto overflow-hidden bg-white">
                  <img
                    src="/images/sv0z24k6_lineup_project_ACT.jpg"
                    alt="Project ACT - Adrenal Crisis Transport"
                    className="w-full h-full object-cover object-center group-hover:scale-[1.02] transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-[#1a1a1a] text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider font-manrope">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E31B23] animate-pulse" />
                    {language === 'fr' ? 'Initiative en vedette' : 'Featured Initiative'}
                  </div>
                </div>

                {/* Content */}
                <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
                  <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-[#1a1a1a] leading-tight">
                    Project ACT{'\u2122'}
                  </h2>
                  <p className="font-manrope text-base sm:text-lg text-[#E31B23] font-semibold mt-1 mb-5">
                    {language === 'fr' ? 'Transport en crise surr\u00e9nalienne' : 'Adrenal Crisis Transport'}
                  </p>

                  <p className="font-manrope text-base text-neutral-600 mb-6 leading-relaxed">
                    {language === 'fr'
                      ? 'Une initiative d\u00e9di\u00e9e \u00e0 am\u00e9liorer la fa\u00e7on dont les kits d\u2019hydrocortisone injectable d\u2019urgence sont transport\u00e9s, prot\u00e9g\u00e9s, identifi\u00e9s et accessibles dans la vie r\u00e9elle.'
                      : 'A dedicated initiative focused on improving how emergency hydrocortisone injection kits are carried, protected, identified, and accessed in real life.'}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-8">
                    {['CortoPod Mini ACT\u2122', 'CortoPod ACT\u2122', 'CortoPod Plus ACT\u2122', language === 'fr' ? 'Kits d\u2019hydrocortisone d\u2019urgence' : 'Emergency hydrocortisone kits'].map((tag) => (
                      <span
                        key={tag}
                        className="bg-neutral-50 border border-neutral-200 text-[#1a1a1a] px-3 py-1 rounded-full text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <span className="inline-flex items-center gap-2 self-start bg-[#1a1a1a] group-hover:bg-[#E31B23] text-white px-6 py-3 rounded-full font-bold text-sm transition-all duration-300">
                    <span>{language === 'fr' ? 'D\u00e9couvrir Project ACT' : 'Learn about Project ACT'}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </div>
            </LocLink>
          </motion.div>
        </div>
      </section>

      {/* Quebec Section */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.quebec}
            alt="Quebec landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1a1a1a]/85" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-[#E31B23] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
                <span className="text-lg">&#127809;</span>
                <span>Made in Canada</span>
              </div>

              <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mb-6">
                {t('quebec.title')}
              </h2>

              <p className="font-manrope text-lg text-neutral-300 leading-relaxed mb-8">
                {t('quebec.description')}
              </p>

              <ul className="grid sm:grid-cols-2 gap-4">
                {t('quebec.features').map((feature, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center gap-3 text-white"
                  >
                    <CheckCircle className="w-5 h-5 text-[#E31B23] flex-shrink-0" />
                    <span className="font-manrope">{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-24 sm:py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1a1a1a] mb-4">
              {t('whoFor.title')}
            </h2>
            <p className="font-manrope text-lg text-neutral-600 max-w-2xl mx-auto">
              {t('whoFor.subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-12">
            {audiences.map((audience, index) => (
              <motion.div
                key={audience.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mb-4">
                  <audience.icon className="w-7 h-7 text-[#E31B23]" />
                </div>
                <h3 className="font-outfit font-bold text-lg text-[#1a1a1a] mb-2">
                  {t(`whoFor.${audience.key}.title`)}
                </h3>
                <p className="font-manrope text-sm text-neutral-600">
                  {t(`whoFor.${audience.key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center bg-white rounded-2xl p-8 border-2 border-red-200">
            <p className="font-manrope text-neutral-600 mb-4">{t('whoFor.partnership')}</p>
            <LocLink
              to="healthcare"
              className="inline-flex items-center space-x-2 text-[#E31B23] hover:text-[#c8171e] font-bold transition-colors"
              data-testid="healthcare-link"
            >
              <span>{t('whoFor.learnMore')}</span>
              <ChevronRight className="w-5 h-5" />
            </LocLink>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-24 sm:py-32 bg-white overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block font-outfit font-bold text-sm uppercase tracking-widest text-[#E31B23] mb-4">
                {t('origin.badge')}
              </span>
              <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1a1a1a] mb-6">
                {t('origin.title')}
              </h2>
              <div className="font-manrope text-neutral-600 leading-relaxed space-y-4">
                {t('origin.description').split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
              <LocLink
                to="story"
                className="inline-flex items-center space-x-2 text-[#E31B23] hover:text-[#c8171e] font-bold mt-8 transition-colors"
                data-testid="read-story-link"
              >
                <span>{t('origin.cta')}</span>
                <ChevronRight className="w-5 h-5" />
              </LocLink>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src={IMAGES.lifestyle}
                alt="Alain Poirier - Fondateur de MyGuardPod avec le NitroPod"
                className="rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Testimonial */}
      <section className="py-24 sm:py-32 bg-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center gap-1 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-[#E31B23] fill-[#E31B23]" />
              ))}
            </div>
            <blockquote className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-light text-white leading-relaxed mb-8">
              {language === 'fr'
                ? '\u00abCe produit a redonn\u00e9 la libert\u00e9 \u00e0 notre famille. C\u2019est maintenant son solide kit d\u2019urgence de superhéros.\u00bb'
                : '\u201cThis product gave our family freedom again. It\u2019s now her sturdy superhero emergency kit.\u201d'}
            </blockquote>
            <cite className="font-manrope text-neutral-400 not-italic text-lg">
              {language === 'fr'
                ? '\u2014 Parent d\u2019un enfant n\u00e9cessitant des injections quotidiennes'
                : '\u2014 Parent of a child requiring daily injections'}
            </cite>
          </motion.div>
        </div>
      </section>

      {/* Etsy customer reviews */}
      <EtsyReviews />

      {/* Product Limitations Notice */}
      <section className="py-12 bg-white border-t border-neutral-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <ProductLimitationsNotice />
        </div>
      </section>
    </main>
  );
};

export default HomePage;
